import type { GlossaryEntry, GlossarySource, GlossaryUnit } from '@/types/glossary';
import type { KnowledgeHub } from '@/types/knowledgeHub';
import { KNOWLEDGE_HUB_BASE } from '../constants/route';

/**
 * The folder every glossary term is filed under.
 *
 * Not one of the standard nine, and deliberately so: the standard folders hold
 * what a tenant writes, and the glossary is reference material the platform
 * ships. It rides in the tree beside them without joining them, which is why
 * it carries no section heading and never reaches the hub's own counts.
 */
export const GLOSSARY_FOLDER = 'glossary';

export const GLOSSARY_LABEL = 'Glossary';

/** Where the folder page for the whole glossary lives. */
export const GLOSSARY_HREF = `${KNOWLEDGE_HUB_BASE}/${GLOSSARY_FOLDER}`;

/** Title-case labels for the metric unit union. */
const UNIT_LABELS: Record<GlossaryUnit, string> = {
  currency: 'Currency',
  percentage: 'Percentage',
  ratio: 'Ratio',
  count: 'Count',
  duration: 'Duration',
  score: 'Score',
};

/**
 * What each source is called.
 *
 * Owned here rather than added to the hub's own subfolder labels: these name
 * connectors, not folders a tenant files into, and the two lists drift apart
 * the moment a connector is added.
 */
export const GLOSSARY_SOURCE_LABELS: Record<GlossarySource, string> = {
  google_ads: 'Google Ads',
  google_search_console: 'Search Console',
  google_analytics: 'Google Analytics',
  hubspot: 'HubSpot',
  linkedin_ads: 'LinkedIn Ads',
  meta_ads: 'Meta Ads',
  salesforce: 'Salesforce',
};

/** A source as a reader knows it, or as it arrived when nothing names it. */
export function glossarySourceLabel(source: string): string {
  return GLOSSARY_SOURCE_LABELS[source as GlossarySource] ?? source;
}

const ID_PREFIX = '__glossary_';
const ID_SUFFIX = '__';

/**
 * The id a term routes on.
 *
 * Shaped to pass as a hub file id — the router tells a file from a folder by
 * the shape of the head segment, and a bare `google_ads.clicks` reads as a
 * folder path. The dot becomes an underscore for the same reason.
 */
export function glossaryFileId(entryId: string): string {
  return `${ID_PREFIX}${entryId.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}${ID_SUFFIX}`;
}

export function isGlossaryFileId(id: string | undefined): boolean {
  return Boolean(id?.startsWith(ID_PREFIX) && id.endsWith(ID_SUFFIX));
}

/** Where a term's page lives. */
export function glossaryFileHref(entryId: string): string {
  return `${KNOWLEDGE_HUB_BASE}/${glossaryFileId(entryId)}`;
}

/** `glossary/google_ads/clicks.md` — the path the tree and folder pages read. */
function glossaryPath(entry: GlossaryEntry): string {
  return `${GLOSSARY_FOLDER}/${entry.source}/${entry.measure}.md`;
}

function bulletList(items: string[]): string {
  return items.map((item) => `- ${item}`).join('\n');
}

/**
 * A term as a document.
 *
 * The definition leads, because that is what the reader came for; how the
 * figure is arrived at and where it can be read follow. Related terms are
 * links to each other's page, so the glossary can be walked rather than
 * searched, the way the dialog's chips used to allow.
 */
export function glossaryMarkdown(entry: GlossaryEntry, byId: Map<string, GlossaryEntry>): string {
  const sections: string[] = [entry.long_description.trim() || entry.short_definition.trim()];

  if (entry.formula?.trim()) {
    sections.push(`## Formula\n\n\`\`\`sql\n${entry.formula.trim()}\n\`\`\``);
  }

  const facts = [
    `**Source** — ${glossarySourceLabel(entry.source)}`,
    `**Measure** — \`${entry.measure}\``,
    entry.unit ? `**Unit** — ${UNIT_LABELS[entry.unit] ?? entry.unit}` : null,
    `**Category** — ${entry.category}`,
    entry.cubes.length > 0 ? `**Available on** — ${entry.cubes.join(', ')}` : null,
  ].filter((fact): fact is string => fact !== null);
  sections.push(`## How it is measured\n\n${bulletList(facts)}`);

  if (entry.examples?.length) {
    sections.push(`## Examples\n\n${bulletList(entry.examples)}`);
  }
  if (entry.notes?.length) {
    sections.push(`## Notes\n\n${bulletList(entry.notes)}`);
  }
  if (entry.related?.length) {
    const links = entry.related.map((id) => {
      const name = byId.get(id)?.name;
      /* A term the tenant cannot see — its source is not connected — has no
         page to open, so it is named rather than linked. */
      return name ? `[${name}](${glossaryFileHref(id)})` : `\`${id}\``;
    });
    sections.push(`## Related metrics\n\n${bulletList(links)}`);
  }

  return `${sections.join('\n\n')}\n`;
}

/**
 * A term as the hub's own file shape.
 *
 * The whole point: once a term is a `KnowledgeHub`, the tree, the folder pages
 * and the file page render it with no glossary-specific screen anywhere. The
 * timestamps are the epoch, which is how the hub already marks a row nothing
 * has written — the file page reads it as "no history to show".
 */
export function toGlossaryFile(
  entry: GlossaryEntry,
  byId: Map<string, GlossaryEntry>,
): KnowledgeHub {
  return {
    id: glossaryFileId(entry.id),
    tenant_id: '',
    path: glossaryPath(entry),
    display_name: entry.name,
    description: entry.short_definition,
    content: glossaryMarkdown(entry, byId),
    frontmatter: {},
    created_at: new Date(0).toISOString(),
    updated_at: new Date(0).toISOString(),
    icon: null,
  };
}

/** Every term the tenant can see, as files, newest naming order preserved. */
export function toGlossaryFiles(entries: GlossaryEntry[] | undefined): KnowledgeHub[] {
  if (!entries?.length) return [];
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  return entries
    .map((entry) => toGlossaryFile(entry, byId))
    .sort((a, b) => a.path.localeCompare(b.path));
}

/** The term a hub file id names, if it names one. */
export function findGlossaryFile(
  files: KnowledgeHub[],
  id: string | undefined,
): KnowledgeHub | undefined {
  if (!isGlossaryFileId(id)) return undefined;
  return files.find((file) => file.id === id);
}
